import React, { useRef, useState, useEffect } from "react";
import LoadingDesignFiles from "./components/LoadingDesignFiles";
import RegistrationTypeSwitch from "./components/RegistrationTypeSwitch";
import DesignUpload from "./components/DesignUpload";
import { scrollToCurrentStep } from "../../utils/scrollToCurrentStep";
import { validateEmail, websiteValidation } from "../../utils/validation";
import {
  uploadFiles,
  uploadFigmaUrl,
  handleUploadError,
} from "../../utils/uploadUtils";
import { validateWebsiteData, scrapeWebsite } from "../../utils/websiteUtils";
import WebsiteAnalysis from "./components/WebsiteAnalysis";
import Button from "../../components/UI/Button";
import FigmaPopup from "modules/ai-quote/components/FigmaPopup";
import axios from "axios";
import { config } from "modules/ai-quote/config";

export function RegistrationStep({
  nextStep,
  userData,
  setUserData,
  choseType,
  handleTypeChange,
}) {
  const [trigger, setTrigger] = useState(false);
  const [popupContent, setPopupContent] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [figmaUrl, setFigmaUrl] = useState("");
  const [inputError, setInputError] = useState("");
  const [uploadForm, setUploadForm] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [fileUploadError, setFileUploadError] = useState([]);
  const [figmaUrlError, setFigmaUrlError] = useState([]);
  const sectionRef = useRef(null);
  const [userWebsiteData, setUserWebsiteData] = useState({
    name: "",
    email: "",
    website: "",
    phone: "",
  });
  const [validationWebsite, setValidationWebsite] = useState({
    name: false,
    email: false,
    website: false,
    phone: false,
  });
  const [scrapperError, setScrapperError] = useState("");
  const [figmaPages, setFigmaPages] = useState([]);

  useEffect(() => {
    if (files.length > 0 && figmaUrl) {
      setInputError(
        "Please upload either design files or a Figma URL, not both"
      );
      scrollToCurrentStep(sectionRef);
    } else {
      setInputError("");
    }
  }, [files, figmaUrl]);

  useEffect(() => {
    if (window.userProfile) {
      const { name, email, phone } = window.userProfile;
      setUploadForm((prevForm) => ({
        ...prevForm,
        name: name || "",
        email: email || "",
        phone: phone || "",
      }));
      setUserWebsiteData((prevData) => ({
        ...prevData,
        name: name || "",
        email: email || "",
        phone: phone || "",
      }));
    }
  }, []);

  const handleValidateWebsite = async () => {
    const isValid = validateWebsiteData(userWebsiteData);

    const HTTP_REFERER = document.referrer;
    const HTTP_USER_AGENT = navigator.userAgent;
    const $_COOKIE = document.cookie;

    setValidationWebsite(isValid);

    if (!isValid.name && !isValid.email && !isValid.website) {
      scrollToCurrentStep(sectionRef);

      setIsLoading(true);

      try {
        const data = await scrapeWebsite(
          userWebsiteData,
          HTTP_REFERER,
          HTTP_USER_AGENT,
          $_COOKIE
        );
        setUserData({
          ...userData,
          id: data.id,
          pages: data.transformedData,
          name: userWebsiteData.name,
          email: userWebsiteData.email,
          phone: userWebsiteData.phone,
          modrewrite: data.modrewrite || "",
        });

        nextStep();
      } catch (error) {
        setScrapperError(error.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDesignUpload = async () => {
    scrollToCurrentStep(sectionRef);

    setIsLoading(true);
    setFileUploadError([]);
    setFigmaUrlError([]);

    const HTTP_REFERER = document.referrer;
    const HTTP_USER_AGENT = navigator.userAgent;
    const $_COOKIE = document.cookie;

    try {
      if (figmaUrl) {
        await sendFigmaUrl();
        setIsLoading(false);
        return;
      }

      const data =
        files.length > 0
          ? await uploadFiles(
              files,
              files,
              uploadForm,
              HTTP_REFERER,
              HTTP_USER_AGENT,
              $_COOKIE
            )
          : await uploadFigmaUrl(figmaUrl, uploadForm);

      setUserData((prevData) => ({
        ...prevData,
        id: data.id,
        name: uploadForm.name,
        email: uploadForm.email,
        phone: uploadForm.phone,
        AIResponse: [data.estimations],
        urls: data.urls,
        originalFiles: data.originalFiles,
        modrewrite: data.modrewrite,
      }));
      nextStep();
    } catch (error) {
      const errors = handleUploadError(error);
      files.length > 0 ? setFileUploadError(errors) : setFigmaUrlError(errors);
    } finally {
      setIsLoading(false);
    }
  };

  const sendFigmaUrl = async () => {
    try {
      setPopupContent([]);

      const response = await axios.post(`${config.BASE_API_URL}/api/v1/figma`, {
        url: figmaUrl,
      });
      setPopupContent(response.data);
      setTrigger(true);
    } catch (error) {
      setFigmaUrlError([error.response.data.message]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUploadForm((prevForm) => ({ ...prevForm, [name]: value }));
  };

  const HTTP_REFERER = document.referrer;
  const HTTP_USER_AGENT = navigator.userAgent;
  const $_COOKIE = document.cookie;

  const handleFigmaUpload = async () => {
    setIsLoading(true);
    setFiles(
      figmaPages.map((image) => ({
        name: image.image,
        preview: image.image,
      }))
    );
    const data = {
      name: uploadForm.name,
      email: uploadForm.email,
      phone: uploadForm.phone,
      type: "figma",
      figmaUrl: figmaUrl,
      files: figmaPages.map((image) => ({
        originalName: image.image,
        path: image.image,
      })),
      HTTP_REFERER,
      HTTP_USER_AGENT,
      $_COOKIE,
    };

    try {
      const result = await axios.post(
        `${config.BASE_API_URL}/api/v1/clients/process-upload-analysis`,
        data
      );
      setUserData((prevData) => ({
        ...prevData,
        id: result.data.id,
        name: uploadForm.name,
        email: uploadForm.email,
        phone: uploadForm.phone,
        AIResponse: [result.data.estimations],
        urls: result.data.urls,
        originalFiles: result.data.originalFiles,
        modrewrite: result.data.modrewrite,
      }));
      nextStep();
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  return (
    <div className="app-ai-quote-registration" ref={sectionRef}>
      {isLoading ? (
        <LoadingDesignFiles files={files} isFigma={figmaUrl} />
      ) : (
        <>
          <RegistrationTypeSwitch
            choseType={choseType}
            handleTypeChange={handleTypeChange}
          />
          {choseType === "design" ? (
            <>
              <div className="app-ai-quote-design" data-aos="fade-up">
                <form
                  className="app-ai-quote-design-form"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <div className="app-ai-quote-form-field">
                    <label htmlFor="name">
                      Name <span>*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={uploadForm.name}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="app-ai-quote-form-field">
                    <label htmlFor="email">
                      Email <span>*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      aria-labelledby="email-control-label"
                      aria-describedby="email-help-text"
                      value={uploadForm.email}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="app-ai-quote-form-field">
                    <label htmlFor="phone">
                      Phone <span>*</span>
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={uploadForm.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                </form>
                <DesignUpload
                  files={files}
                  setFiles={setFiles}
                  formUpload={handleDesignUpload}
                  setFigmaUrl={setFigmaUrl}
                  figmaUrl={figmaUrl}
                  fileUploadError={fileUploadError}
                  figmaUrlError={figmaUrlError}
                />
                {inputError && (
                  <div className="app-ai-quote-error-message">
                    <p>{inputError}</p>
                  </div>
                )}
                <p>The estimate will be sent to your email address.</p>
                <p>Please note, the AI-generated quote may not be final as parsing could be affected by design specifics. The final estimate will be confirmed by your project manager after reviewing the designs and specifications.</p>
                <div className="app-ai-quote-button-group">
                  <Button
                    type="submit"
                    onClick={handleDesignUpload}
                    disabled={
                      uploadForm.name.length < 2 ||
                      !validateEmail(uploadForm.email) ||
                      (!files.length && !websiteValidation(figmaUrl)) ||
                      !uploadForm.phone ||
                      inputError !== ""
                    }
                  >
                    Calculate Now
                  </Button>
                  <p>
                    Pressing this button indicates your consent
                    <br /> to our{" "}
                    <a href="https://getdevdone.com/terms-and-conditions.html">
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a href="https://getdevdone.com/privacy-policy.html">
                      Privacy Policy
                    </a>
                    .
                  </p>
                </div>
              </div>
            </>
          ) : (
            <WebsiteAnalysis
              userData={userWebsiteData}
              formUpload={handleValidateWebsite}
              setUserWebsiteData={setUserWebsiteData}
              validationWebsite={validationWebsite}
              scrapperError={scrapperError}
            />
          )}
          {trigger && (
            <FigmaPopup
              trigger={trigger}
              setTrigger={setTrigger}
              popupContent={popupContent}
              nextStep={nextStep}
              uploadForm={uploadForm}
              setUserData={setUserData}
              setFigmaPages={setFigmaPages}
              figmaPages={figmaPages}
              handleFigmaUpload={handleFigmaUpload}
            />
          )}
        </>
      )}
    </div>
  );
}
